import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  confirmOrder,
  confirmOrderDelivery,
  fetchOrder,
  rejectOrder,
  sendOrderChat,
  updateOrderStatus,
  uploadPaymentSlip,
  verifyPaymentSlip,
} from '../../api/orders';
import PaymentSlipSection from '../payments/PaymentSlipSection';
import { getPaymentPolicy, salonBlockedBySlip } from '../../utils/paymentSlip';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';
import OrderStatusStepper from './OrderStatusStepper';
import InvoiceActions from '../invoices/InvoiceActions';
import OrderPricingManager, { OrderPricingBreakdown } from '../pricing/OrderPricingManager';

const STATUS_ACTIONS = [
  { from: 'CONFIRMED', status: 'PACKING', labelKey: 'orders.actions.startPacking' },
  { from: 'PACKING', status: 'SHIPPED', labelKey: 'orders.actions.markShipped' },
  { from: 'SHIPPED', status: 'OUT_FOR_DELIVERY', labelKey: 'orders.actions.outForDelivery' },
  { from: 'OUT_FOR_DELIVERY', status: 'DELIVERED', labelKey: 'orders.actions.delivered' },
];

const OrderDetailDrawer = ({ orderId, onClose, salonMode }) => {
  const { t } = useTranslation();
  const { user, role } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const chatEndRef = useRef(null);

  const { data, refetch } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => fetchOrder(orderId),
    enabled: !!orderId,
    refetchInterval: 5000,
  });

  const order = data?.order;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    queryClient.invalidateQueries({ queryKey: ['order-summary'] });
  };

  const slipUploadMut = useMutation({
    mutationFn: ({ file, bankAccountId }) => uploadPaymentSlip(orderId, file, { bankAccountId }),
    onSuccess: () => {
      showToast(t('toast.paymentSlipUploaded'), 'success');
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('errors.uploadFailed')), 'error'),
  });

  const confirmMut = useMutation({
    mutationFn: () => confirmOrder(orderId),
    onSuccess: () => { showToast(t('toast.orderConfirmed'), 'success'); invalidate(); },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const statusMut = useMutation({
    mutationFn: (status) => updateOrderStatus(orderId, status),
    onSuccess: () => { showToast(t('toast.statusUpdated'), 'success'); invalidate(); },
  });

  const rejectMut = useMutation({
    mutationFn: () => rejectOrder(orderId, rejectReason),
    onSuccess: () => { showToast(t('toast.orderRejected'), 'success'); invalidate(); },
  });

  const verifyMut = useMutation({
    mutationFn: (index) => verifyPaymentSlip(orderId, index),
    onSuccess: () => { showToast(t('toast.paymentVerified'), 'success'); invalidate(); },
  });

  const chatMut = useMutation({
    mutationFn: (text) => sendOrderChat(orderId, text),
    onSuccess: () => { setMessage(''); refetch(); },
  });

  const deliveryMut = useMutation({
    mutationFn: () => confirmOrderDelivery(orderId),
    onSuccess: () => {
      showToast(t('toast.orderDelivered'), 'success');
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('toast.errorGeneric')), 'error'),
  });

  const canConfirmDelivery =
    !salonMode &&
    order &&
    ['PACKING', 'SHIPPED', 'OUT_FOR_DELIVERY'].includes(order.status);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [order?.chat]);

  if (!orderId) return null;

  const clientName = order?.clientId
    ? `${order.clientId.firstName} ${order.clientId.lastName}`
    : '';

  const nextAction = STATUS_ACTIONS.find((a) => a.from === order?.status);

  const canManageOrders =
    salonMode &&
    (['SALON_MANAGER', 'SUPER_ADMIN'].includes(role) ||
      user?.canManageOrders ||
      user?.permissions?.canManageOrders);

  const paymentBlocked = order
    ? salonBlockedBySlip(order, getPaymentPolicy(order))
    : false;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">
            {t('orders.drawer.orderNumber', { number: orderId?.slice(-8).toUpperCase() })}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {!order && <p className="p-5 text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

        {order && (
          <>
            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              {salonMode && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('orders.drawer.clientLabel', { name: clientName })}
                </p>
              )}
              <OrderStatusStepper status={order.status} />

              {canManageOrders ? (
                <OrderPricingManager order={order} onUpdated={invalidate} />
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium">{t('orders.drawer.items')}</p>
                  {order.items.map((item, i) => (
                    <div key={i} className="flex gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
                      {item.productId?.images?.[0]?.url && (
                        <img src={item.productId.images[0].url} alt="" className="h-14 w-14 rounded object-cover" />
                      )}
                      <div className="flex-1 text-sm">
                        <p className="font-medium">{item.productName}</p>
                        <p className="text-zinc-600 dark:text-zinc-500">× {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                  <OrderPricingBreakdown order={order} />
                </div>
              )}

              {order.status === 'DELIVERED' && (
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                  <p className="mb-2 text-sm font-medium">{t('invoice.title')}</p>
                  <InvoiceActions kind="order" entityId={orderId} />
                </div>
              )}

              <PaymentSlipSection
                entity={order}
                bankAccounts={order.bankAccounts || []}
                salonMode={salonMode}
                canManage={canManageOrders}
                onUpload={(file, bankAccountId) => slipUploadMut.mutate({ file, bankAccountId })}
                onVerify={(index) => verifyMut.mutate(index)}
                uploading={slipUploadMut.isPending}
              />

              {canConfirmDelivery && (
                <div className="rounded-lg border border-emerald-800/40 bg-emerald-950/20 p-3">
                  <p className="text-sm text-emerald-200/90">
                    {t('orders.drawer.confirmDeliveryHint')}
                  </p>
                  <button
                    type="button"
                    disabled={deliveryMut.isPending}
                    onClick={() => deliveryMut.mutate()}
                    className="mt-3 w-full rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {deliveryMut.isPending ? t('orders.drawer.confirming') : t('orders.drawer.receivedOrder')}
                  </button>
                </div>
              )}

              {salonMode && order.status === 'PENDING' && (
                <div className="space-y-2">
                  {paymentBlocked && (
                    <p className="text-xs text-red-400">{t('payments.salonWaitingForSlip')}</p>
                  )}
                  <button
                    type="button"
                    disabled={paymentBlocked || confirmMut.isPending}
                    onClick={() => confirmMut.mutate()}
                    className="w-full rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {t('orders.drawer.confirmOrder')}
                  </button>
                </div>
              )}

              {salonMode && nextAction && (
                <button
                  type="button"
                  onClick={() => statusMut.mutate(nextAction.status)}
                  className="w-full rounded-lg border border-glow-600 py-2 text-sm text-glow-300"
                >
                  {t(nextAction.labelKey)}
                </button>
              )}

              {salonMode && ['PENDING', 'CONFIRMED'].includes(order.status) && (
                <div className="space-y-2">
                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={t('orders.drawer.rejectReason')}
                    className="w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => rejectMut.mutate()}
                    className="w-full rounded-lg border border-red-800 py-2 text-sm text-red-400"
                  >
                    {t('orders.drawer.rejectOrder')}
                  </button>
                </div>
              )}

              <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3 text-sm text-zinc-600 dark:text-zinc-500">
                <p>{order.shippingAddress?.street}</p>
                <p>{order.shippingAddress?.city} {order.shippingAddress?.postalCode}</p>
                <p>{order.shippingAddress?.phone}</p>
                {order.notes && <p className="mt-2 italic">{order.notes}</p>}
              </div>
            </div>

            <div className="border-t border-zinc-200 dark:border-zinc-800 p-4">
              <p className="mb-2 text-xs font-medium text-zinc-600 dark:text-zinc-500">{t('common.chat')}</p>
              <div className="mb-3 max-h-40 space-y-2 overflow-y-auto text-sm">
                {(order.chat || []).map((msg, i) => (
                  <div
                    key={i}
                    className={`rounded-lg px-3 py-2 ${
                      msg.senderId?.toString() === user?.id?.toString() ? 'ml-8 bg-glow-600/20' : 'mr-8 bg-zinc-100 dark:bg-zinc-800'
                    }`}
                  >
                    <p className="text-xs text-zinc-600 dark:text-zinc-500">
                      {t(`roles.${msg.senderRole}`, { defaultValue: msg.senderRole })}
                    </p>
                    <p>{msg.message}</p>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (message.trim()) chatMut.mutate(message.trim());
                }}
              >
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                  placeholder={t('orders.drawer.messagePlaceholder')}
                />
                <button type="submit" className="rounded-lg bg-glow-600 px-3 py-2 text-sm text-white">
                  {t('common.send')}
                </button>
              </form>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default OrderDetailDrawer;
